export interface CSQuestion {
  id: string;
  question: string;
  /** Templated gap copy returned when this is one of the user's weakest answers. */
  gap: string;
  options: [string, string, string, string]; // mapped to 1..4 points
}

export const CONTENT_SCORE_QUESTIONS: CSQuestion[] = [
  {
    id: "planning",
    question: "How far ahead is your content usually planned?",
    gap: "Content is planned too close to publish, which leaves little room for strategy or repurposing.",
    options: ["Same day", "A few days", "1 to 2 weeks", "1 month or more"],
  },
  {
    id: "pillars",
    question: "Do you have defined content pillars?",
    gap: "There are no clear content pillars guiding the calendar, so every post starts from scratch.",
    options: [
      "No",
      "Sort of, but they are not documented",
      "Yes, but we do not use them consistently",
      "Yes, and they guide our calendar",
    ],
  },
  {
    id: "repurpose",
    question: "How often do you repurpose long-form content?",
    gap: "Long-form content isn't being repurposed, so most of its potential reach is wasted.",
    options: ["Never", "Occasionally", "Often", "It is built into our workflow"],
  },
  {
    id: "metrics",
    question: "Do you track performance beyond impressions and likes?",
    gap: "Performance tracking stops at vanity metrics, leaving no signal to adjust strategy.",
    options: [
      "No",
      "Sometimes",
      "Yes, but inconsistently",
      "Yes, and we use it to adjust strategy",
    ],
  },
  {
    id: "workflow",
    question: "Do you have a repeatable approval and publishing workflow?",
    gap: "There's no repeatable approval and publishing workflow, so output depends on who is around.",
    options: ["No", "Informal only", "Mostly", "Yes"],
  },
  {
    id: "businessGoals",
    question: "Is your content tied to business goals?",
    gap: "Content isn't clearly tied to business goals, which makes ROI hard to defend.",
    options: ["Not really", "Sometimes", "Usually", "Yes, clearly"],
  },
  {
    id: "voice",
    question: "How consistent is your brand voice across platforms?",
    gap: "Brand voice drifts across platforms, which weakens recognition and trust.",
    options: ["Inconsistent", "Somewhat consistent", "Mostly consistent", "Very consistent"],
  },
  {
    id: "ai",
    question: "Do you use AI in your content workflow?",
    gap: "AI is either unused or applied ad hoc, so you're missing speed and quality leverage.",
    options: [
      "No",
      "Randomly",
      "Yes, for drafting or ideation",
      "Yes, with a clear workflow and quality control",
    ],
  },
  {
    id: "trust",
    question: "How often does your content explain why your audience should trust you?",
    gap: "Trust-building moments are rare in the feed, so the audience can't tell why you're credible.",
    options: ["Rarely", "Sometimes", "Often", "Consistently"],
  },
  {
    id: "operation",
    question: "How would you describe your current content operation?",
    gap: "The operation is reactive rather than repeatable, so every week feels like a scramble.",
    options: ["Random posting", "Reactive posting", "Planned content", "Repeatable content system"],
  },
];

export type CSLevelKey = "random" | "reactive" | "planned" | "engine";

export interface CSLevel {
  key: CSLevelKey;
  label: string;
  min: number; // inclusive raw points
  max: number; // inclusive raw points
  diagnosis: string;
  nextStep: string;
  cta: { headline: string; body: string; primaryLabel: string; secondaryLabel: string };
}

export const CS_LEVELS: CSLevel[] = [
  {
    key: "random",
    label: "Level 1: Random Posting",
    min: 10,
    max: 17,
    diagnosis:
      "Your content is mostly activity without a clear system. The biggest risk is wasted effort. You may be posting, but the content likely is not connected to clear goals, repeatable workflows, or measurable business outcomes.",
    nextStep:
      "Start with content pillars, one repeatable publishing rhythm, and a basic way to measure what's actually working.",
    cta: {
      headline: "Turn scattered posting into a real content system.",
      body: "Scott can help clarify your content pillars, build a repeatable workflow, improve messaging, and connect content to business goals.",
      primaryLabel: "Improve My Content System",
      secondaryLabel: "View Services",
    },
  },
  {
    key: "reactive",
    label: "Level 2: Reactive Content",
    min: 18,
    max: 25,
    diagnosis:
      "You are creating content, but the process likely depends too much on urgency, available time, or scattered ideas. The opportunity is to turn reactive execution into a more reliable system.",
    nextStep:
      "Build a rolling calendar covering 2 to 4 weeks, sharpen messaging around your pillars, and add reporting that goes beyond impressions and likes.",
    cta: {
      headline: "Move from reactive content to repeatable execution.",
      body: "Scott can help you build a clearer calendar, stronger messaging, better repurposing workflows, and reporting that goes beyond vanity metrics.",
      primaryLabel: "Build a Better Content System",
      secondaryLabel: "View Services",
    },
  },
  {
    key: "planned",
    label: "Level 3: Planned Content",
    min: 26,
    max: 33,
    diagnosis:
      "You have a solid foundation. The next opportunity is making the system more repeatable, measurable, and easier to scale across platforms without adding unnecessary workload.",
    nextStep:
      "Tighten repurposing workflows, align content with SEO, and connect every piece to a clearer business outcome.",
    cta: {
      headline: "You have the foundation. Now make it easier to scale.",
      body: "Scott can help optimize your pillars, improve SEO alignment, sharpen content workflows, strengthen repurposing, and connect your content to clearer business outcomes.",
      primaryLabel: "Optimize My Content System",
      secondaryLabel: "View Services",
    },
  },
  {
    key: "engine",
    label: "Level 4: Content Engine",
    min: 34,
    max: 40,
    diagnosis:
      "You're running a real content engine. The fundamentals are in place: clear pillars, a repeatable workflow, measurable outcomes, and a consistent voice. The next move isn't fixing gaps, it's protecting the system so it keeps running without you in every detail.",
    nextStep:
      "Document the system so someone else can run it, hand off execution where it makes sense, and use your time on strategy, thought leadership, and the work only you can do.",
    cta: {
      headline: "Your system is working. Now make it run without you.",
      body: "Scott can step in as a fractional content lead or strategist, document your engine so it survives team changes, and free you up from day-to-day execution.",
      primaryLabel: "Hand Off My Content System",
      secondaryLabel: "View Services",
    },
  },
];

export function scoreContentSystem(answers: Record<string, number>) {
  const total = CONTENT_SCORE_QUESTIONS.reduce((sum, q) => sum + (answers[q.id] ?? 0), 0);
  const percentage = Math.round((total / 40) * 100);
  const level = CS_LEVELS.find((l) => total >= l.min && total <= l.max) ?? CS_LEVELS[0];

  // Real gaps only: a top-tier answer (4 pts) isn't a gap. Sort ascending so the
  // weakest answers come first, then take up to 3.
  const ranked = [...CONTENT_SCORE_QUESTIONS]
    .map((q) => ({ q, points: answers[q.id] ?? 0 }))
    .filter((r) => r.points < 4)
    .sort(
      (a, b) =>
        a.points - b.points ||
        CONTENT_SCORE_QUESTIONS.indexOf(a.q) - CONTENT_SCORE_QUESTIONS.indexOf(b.q),
    );
  const gaps = ranked.slice(0, 3).map((r) => r.q.gap);

  return { total, percentage, level, gaps };
}
